import { useAuth } from "context/AuthContext";

export type MonitoringPermission =
  | "USER_MANAGE"
  | "MANAGE_MONITORING_COMPANIES"
  | "SUBMIT_MONITORING_FORMS"
  | "REVIEW_MONITORING"
  | "REASSIGN_APPLICATIONS"
  | "MANAGE_PAYMENTS"
  | "MANAGE_MONITORING_AGREEMENTS"
  | "DELETE_MONITORING_AGREEMENTS"
  | "UPLOAD_ADDITIONAL_DOCUMENTS";

/**
 * Single source of truth for all role/permission checks.
 * Replaces all localStorage.getItem("role") calls throughout the app.
 */
export function useAccess() {
  const { user } = useAuth();

  if (!user) {
    return {
      isAgency: false,
      isMonitoring: false,
      isAdmin: false,
      isMember: false,
      isHead: false,
      isDirector: false,
      canSubmitApp: false,
      canReviewApp: false,
      canManageOrgs: false,
      canManageUsers: false,
      canReassignApps: false,
      canManageAgreements: false,
      canDeleteAgreements: false,
      canManagePayments: false,
      canUploadAdditionalDocuments: false,
      canSeeAllApps: false,
      // Monitoring type
      isUniversal: false,
      isCgs: false,
      activeAssignmentTypes: [] as ("universal" | "cgs")[],
      hasPermission: (_p: MonitoringPermission) => false,
    };
  }

  const isAgency     = user.user_type === "agency";
  const isMonitoring = user.user_type === "monitoring";
  const isAdmin      = user.user_type === "admin";
  const isMember     = user.org_role === "member";
  const isHead       = user.org_role === "head";
  const isDirector   = false;

  const activeAssignmentTypes = (user.activeAssignments ?? []).map((a) => a.application_type);

  return {
    isAgency,
    isMonitoring,
    isAdmin,
    isMember,
    isHead,
    isDirector,
    // Monitoring company type (from active assignments)
    isUniversal: isMonitoring && activeAssignmentTypes.includes("universal"),
    isCgs:       isMonitoring && activeAssignmentTypes.includes("cgs"),
    activeAssignmentTypes,
    // Action permissions
    canSubmitApp:    user.permissions.includes("SUBMIT_MONITORING_FORMS"),
    canReviewApp:    user.permissions.includes("REVIEW_MONITORING"),
    canManageOrgs:     isAgency && user.permissions.includes("MANAGE_MONITORING_COMPANIES"),
    canManageUsers:    user.permissions.includes("USER_MANAGE") ||
                       (isMonitoring && user.org_role === "head") ||
                       (isAgency && user.org_role === "head"),
    canReassignApps:   isAgency && user.permissions.includes("REASSIGN_APPLICATIONS"),
    canManageAgreements: isAgency && user.permissions.includes("MANAGE_MONITORING_AGREEMENTS"),
    canDeleteAgreements: isAgency && user.permissions.includes("DELETE_MONITORING_AGREEMENTS"),
    canManagePayments: user.permissions.includes("MANAGE_PAYMENTS"),
    canUploadAdditionalDocuments: isAgency && user.permissions.includes("UPLOAD_ADDITIONAL_DOCUMENTS"),
    canSeeAllApps:   isAgency || isAdmin,
    hasPermission: (p: MonitoringPermission) => user.permissions.includes(p),
  };
}

export default useAccess;
